/**
 * Early CMMD iframe handoff. Runs before React mounts so the auth gate sees a
 * session cookie on the first paint.
 *
 * Protocol:
 * 1. iframe loads `/?embed=1`
 * 2. child posts `{ type: "cmmd-forge-embed-ready" }` to the parent
 * 3. parent posts `{ type: "cmmd-forge-embed-handoff", code }` (one-time code only)
 * 4. child `POST /api/auth/cmmd-embed/exchange` with `{ code }`
 * 5. on success, bind workspace id to the redeemed organization and continue React boot
 *
 * Always posts ready so a CMMD workspace switch can re-bind the org. When a
 * prior embed session already exists, a short timeout keeps boot fast while
 * still accepting a fresh handoff if the parent sends one.
 */

import { isCmmdEmbedMode, persistCmmdEmbedMode } from "./cmmdEmbedMode";
import { ensureCmmdEmbedHostCommandEarlyListener } from "./cmmdEmbedHostCommands";
import {
  alreadyHasEmbedSession,
  bindEmbedForgeWorkspaceId,
  readStoredEmbedOrganizationId,
} from "./cmmdEmbedWorkspaceBind";

export const CMMD_EMBED_READY_TYPE = "cmmd-forge-embed-ready";
export const CMMD_EMBED_HANDOFF_TYPE = "cmmd-forge-embed-handoff";

const HANDOFF_TIMEOUT_MS = 15_000;
const RESUME_HANDOFF_TIMEOUT_MS = 2_500;

type EmbedHandoffMessage = {
  readonly type: typeof CMMD_EMBED_HANDOFF_TYPE;
  readonly code: string;
};

function isCmmdHost(hostname: string): boolean {
  return hostname === "cmmd.ai" || hostname.endsWith(".cmmd.ai");
}

function isLocalCmmdDevHost(hostname: string, port: string): boolean {
  const localName = hostname === "localhost" || hostname === "127.0.0.1";
  const localPort = port === "3015" || port === "3000" || port === "";
  return localName && localPort;
}

function isTrustedCmmdOrigin(origin: string): boolean {
  try {
    const url = new URL(origin);
    if (isCmmdHost(url.hostname)) return url.protocol === "https:";
    return isLocalCmmdDevHost(url.hostname, url.port) && url.protocol.startsWith("http");
  } catch {
    return false;
  }
}

function readHandoffMessage(data: unknown): EmbedHandoffMessage | null {
  if (typeof data !== "object" || data === null) return null;
  const record = data as Record<string, unknown>;
  if (record.type !== CMMD_EMBED_HANDOFF_TYPE) return null;
  const code = typeof record.code === "string" ? record.code.trim() : "";
  if (code.length < 16) return null;
  return { type: CMMD_EMBED_HANDOFF_TYPE, code };
}

async function exchangeEmbedCode(code: string): Promise<{ organizationId: number } | null> {
  const response = await fetch("/api/auth/cmmd-embed/exchange", {
    method: "POST",
    credentials: "include",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({ code }),
  });
  if (!response.ok) return null;
  const body = (await response.json()) as { organizationId?: unknown };
  const organizationId =
    typeof body.organizationId === "number" && Number.isFinite(body.organizationId)
      ? body.organizationId
      : null;
  if (organizationId === null) return null;
  return { organizationId };
}

/**
 * If this window is a CMMD embed, wait for the parent handoff (or resume the
 * stored org) and bind `currentForgeWorkspaceId` before React mounts.
 */
export async function runCmmdEmbedHandoffBeforeBoot(): Promise<void> {
  if (typeof window === "undefined") return;
  if (!isCmmdEmbedMode()) return;
  if (window.parent === window) return;

  // Sticky for subsequent client navigations that drop `?embed=1`.
  persistCmmdEmbedMode();
  ensureCmmdEmbedHostCommandEarlyListener();

  const storedOrg = readStoredEmbedOrganizationId();
  const resuming = alreadyHasEmbedSession() && storedOrg !== null;
  if (resuming && storedOrg !== null) {
    bindEmbedForgeWorkspaceId(storedOrg);
  }

  const parentOrigin = document.referrer
    ? (() => {
        try {
          return new URL(document.referrer).origin;
        } catch {
          return "*";
        }
      })()
    : "*";

  window.parent.postMessage({ type: CMMD_EMBED_READY_TYPE, cmmdEmbedded: true }, parentOrigin);

  const result = await new Promise<{ organizationId: number } | null>((resolve) => {
    let settled = false;
    const finish = (value: { organizationId: number } | null) => {
      if (settled) return;
      settled = true;
      window.clearTimeout(timeoutId);
      window.removeEventListener("message", onMessage);
      resolve(value);
    };

    const timeoutId = window.setTimeout(
      () => finish(null),
      resuming ? RESUME_HANDOFF_TIMEOUT_MS : HANDOFF_TIMEOUT_MS,
    );

    const onMessage = (event: MessageEvent) => {
      if (!isTrustedCmmdOrigin(event.origin)) return;
      const handoff = readHandoffMessage(event.data);
      if (!handoff) return;
      void exchangeEmbedCode(handoff.code)
        .then((exchanged) => finish(exchanged))
        .catch(() => finish(null));
    };

    window.addEventListener("message", onMessage);
  });

  if (!result) return;

  bindEmbedForgeWorkspaceId(result.organizationId);
}
